<template>
  <layouts></layouts>
  <student-breadcrumb :title="title" :text="text" :text1="text1" />

  <div class="page-content course-sec">
    <div class="container">
      <div class="row align-items-center" style="margin-top: 50px;">
        <div class="col-md-12">
          <div class="add-course-header">
            <h2>{{ isEditMode ? 'Edit Quiz' : 'Create New Quiz' }}</h2>
            <div class="add-course-btns">
              <ul class="nav">
                <li>
                  <router-link to="/caduceus/quizzes" class="btn btn-black">
                    Back to Quizzes
                  </router-link>
                </li>
                <li>
                  <button @click="handleSave" class="btn btn-success-dark" :disabled="saving">
                    <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
                    Save Quiz
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <div class="card">
            <div class="widget-set">
              <div class="widget-content">
                <div class="add-course-info">
                  <div class="add-course-inner-header">
                    <h4>Quiz Information</h4>
                  </div>
                  <div class="add-course-form">
                    <form @submit.prevent="handleSave">
                      <div class="form-group">
                        <label class="add-course-label">Title *</label>
                        <input
                          type="text"
                          class="form-control"
                          placeholder="Enter quiz title"
                          v-model="quizData.title"
                          required
                        />
                      </div>

                      <div class="form-group">
                        <label class="add-course-label">Course *</label>
                        <select
                          v-model="quizData.courseId"
                          class="form-select"
                          required
                          :disabled="isEditMode"
                        >
                          <option :value="null">Select a course</option>
                          <option
                            v-for="course in courses"
                            :key="course.id"
                            :value="course.id"
                          >
                            {{ course.title }}
                          </option>
                        </select>
                      </div>

                      <div class="form-group">
                        <label class="add-course-label">Description</label>
                        <textarea
                          class="form-control"
                          rows="4"
                          placeholder="Enter quiz description"
                          v-model="quizData.description"
                        ></textarea>
                      </div>
                    </form>
                  </div>
                </div>

                <div class="add-course-info mt-4">
                  <div class="add-course-inner-header d-flex justify-content-between align-items-center">
                    <h4>Questions</h4>
                    <button @click="addQuestion" class="btn btn-primary btn-sm">
                      <i class="bx bx-plus"></i> Add Question
                    </button>
                  </div>

                  <div v-if="quizData.questions.length === 0" class="text-center py-5">
                    <p class="text-muted">No questions added yet. Click "Add Question" to start.</p>
                  </div>

                  <draggable
                    v-else
                    v-model="quizData.questions"
                    item-key="tempId"
                    handle=".drag-handle"
                    @end="updateQuestionOrder"
                    class="questions-list"
                  >
                    <template #item="{ element: question, index }">
                      <div class="question-card mb-3">
                        <div class="question-header">
                          <div class="d-flex align-items-center">
                            <span class="drag-handle me-2">
                              <i class="bx bx-menu"></i>
                            </span>
                            <span class="question-number">Question {{ index + 1 }}</span>
                          </div>
                          <button
                            @click="removeQuestion(index)"
                            class="btn btn-sm btn-outline-danger"
                            type="button"
                          >
                            <i class="bx bx-trash"></i>
                          </button>
                        </div>

                        <div class="question-body">
                          <div class="form-group">
                            <label>Question Text *</label>
                            <textarea
                              class="form-control"
                              rows="2"
                              placeholder="Enter question text"
                              v-model="question.text"
                              required
                            ></textarea>
                          </div>

                          <div class="form-group">
                            <label>Question Type</label>
                            <select
                              v-model="question.type"
                              class="form-select"
                              @change="handleQuestionTypeChange(question)"
                            >
                              <option value="SINGLE_CHOICE">Single Choice</option>
                              <option value="MULTIPLE_CHOICE">Multiple Choice</option>
                            </select>
                          </div>

                          <div class="form-group">
                            <label>Options *</label>
                            <div
                              v-for="(option, optIndex) in question.options"
                              :key="optIndex"
                              class="option-item mb-2"
                            >
                              <div class="input-group">
                                <div class="input-group-text">
                                  <input
                                    v-if="question.type === 'SINGLE_CHOICE'"
                                    type="radio"
                                    :name="`question-${question.tempId}`"
                                    :checked="option.isCorrect"
                                    @change="setCorrectOption(question, optIndex)"
                                  />
                                  <input
                                    v-else
                                    type="checkbox"
                                    v-model="option.isCorrect"
                                  />
                                </div>
                                <input
                                  type="text"
                                  class="form-control"
                                  :placeholder="`Option ${String.fromCharCode(65 + optIndex)}`"
                                  v-model="option.text"
                                  required
                                />
                                <button
                                  v-if="question.options.length > 2"
                                  @click="removeOption(question, optIndex)"
                                  class="btn btn-outline-danger"
                                  type="button"
                                >
                                  <i class="bx bx-x"></i>
                                </button>
                              </div>
                            </div>
                            <button
                              v-if="question.options.length < 6"
                              @click="addOption(question)"
                              class="btn btn-sm btn-outline-secondary mt-2"
                              type="button"
                            >
                              <i class="bx bx-plus"></i> Add Option
                            </button>
                          </div>

                          <div class="form-group mb-0">
                            <label>Feedback (Optional)</label>
                            <textarea
                              class="form-control"
                              rows="2"
                              placeholder="Provide feedback for this question"
                              v-model="question.feedback"
                            ></textarea>
                          </div>
                        </div>
                      </div>
                    </template>
                  </draggable>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <layouts1></layouts1>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import draggable from 'vuedraggable';
import QuizService from '@/services/quiz.service';
import ApiService from '@/services/ApiService';
import Swal from 'sweetalert2';

const route = useRoute();
const router = useRouter();

const title = ref('Quiz Form');
const text = ref('Home');
const text1 = ref('Quiz');

const isEditMode = computed(() => !!route.params.id);
const quizId = computed(() => parseInt(route.params.id));

const courses = ref([]);
const saving = ref(false);
const loading = ref(false);

let questionCounter = 0;

const quizData = ref({
  title: '',
  description: '',
  courseId: null,
  questions: []
});

const createEmptyQuestion = () => ({
  tempId: `temp-${Date.now()}-${questionCounter++}`,
  text: '',
  type: 'SINGLE_CHOICE',
  feedback: '',
  order: quizData.value.questions.length,
  options: [
    { text: '', isCorrect: false },
    { text: '', isCorrect: false },
    { text: '', isCorrect: false },
    { text: '', isCorrect: false }
  ]
});

const addQuestion = () => {
  quizData.value.questions.push(createEmptyQuestion());
};

const removeQuestion = async (index) => {
  const result = await Swal.fire({
    title: 'Remove Question?',
    text: 'Are you sure you want to remove this question?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Yes, remove it',
    cancelButtonText: 'Cancel'
  });

  if (result.isConfirmed) {
    quizData.value.questions.splice(index, 1);
    updateQuestionOrder();
  }
};

const addOption = (question) => {
  question.options.push({ text: '', isCorrect: false });
};

const removeOption = (question, optIndex) => {
  question.options.splice(optIndex, 1);
};

const setCorrectOption = (question, optIndex) => {
  question.options.forEach((opt, idx) => {
    opt.isCorrect = idx === optIndex;
  });
};

const handleQuestionTypeChange = (question) => {
  if (question.type === 'SINGLE_CHOICE') {
    const correctCount = question.options.filter(opt => opt.isCorrect).length;
    if (correctCount > 1) {
      question.options.forEach((opt, idx) => {
        opt.isCorrect = idx === 0;
      });
    }
  }
};

const updateQuestionOrder = () => {
  quizData.value.questions.forEach((question, index) => {
    question.order = index;
  });
};

const fetchCourses = async () => {
  try {
    const response = await ApiService.get('/courses/me');
    courses.value = response.data || [];
  } catch (err) {
    console.error('Error fetching courses:', err);
  }
};

const fetchQuiz = async () => {
  if (!isEditMode.value) return;

  loading.value = true;
  try {
    const response = await QuizService.getById(quizId.value);
    const quiz = response.data;

    quizData.value = {
      title: quiz.title,
      description: quiz.description || '',
      courseId: quiz.courseId,
      questions: quiz.questions?.map((q, index) => ({
        id: q.id,
        tempId: `existing-${q.id}`,
        text: q.text,
        type: q.type,
        feedback: q.feedback || '',
        order: q.order ?? index,
        options: q.options?.map(opt => ({
          id: opt.id,
          text: opt.text,
          isCorrect: opt.isCorrect
        })) || []
      })) || []
    };

    questionCounter = quizData.value.questions.length;
  } catch (err) {
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: 'Failed to load quiz'
    });
    router.push('/caduceus/quizzes');
  } finally {
    loading.value = false;
  }
};

const validateQuiz = () => {
  if (!quizData.value.title.trim()) {
    Swal.fire({
      icon: 'warning',
      title: 'Validation Error',
      text: 'Please enter a quiz title'
    });
    return false;
  }

  if (!quizData.value.courseId) {
    Swal.fire({
      icon: 'warning',
      title: 'Validation Error',
      text: 'Please select a course'
    });
    return false;
  }

  if (quizData.value.questions.length === 0) {
    Swal.fire({
      icon: 'warning',
      title: 'Validation Error',
      text: 'Please add at least one question'
    });
    return false;
  }

  for (let i = 0; i < quizData.value.questions.length; i++) {
    const question = quizData.value.questions[i];

    if (!question.text.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: `Question ${i + 1}: Please enter question text`
      });
      return false;
    }

    if (question.options.length < 2) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: `Question ${i + 1}: Please add at least 2 options`
      });
      return false;
    }

    const emptyOptions = question.options.filter(opt => !opt.text.trim());
    if (emptyOptions.length > 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: `Question ${i + 1}: All options must have text`
      });
      return false;
    }

    const correctOptions = question.options.filter(opt => opt.isCorrect);
    if (correctOptions.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: `Question ${i + 1}: Please mark at least one correct answer`
      });
      return false;
    }

    if (question.type === 'SINGLE_CHOICE' && correctOptions.length > 1) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: `Question ${i + 1}: Single choice questions must have exactly one correct answer`
      });
      return false;
    }
  }

  return true;
};

const handleSave = async () => {
  if (!validateQuiz()) return;

  saving.value = true;

  try {
    const payload = {
      title: quizData.value.title,
      description: quizData.value.description,
      courseId: quizData.value.courseId
    };

    let savedQuiz;

    if (isEditMode.value) {
      await QuizService.update(quizId.value, payload);

      for (const question of quizData.value.questions) {
        const questionPayload = {
          text: question.text,
          type: question.type,
          feedback: question.feedback,
          options: question.options.map(opt => ({
            text: opt.text,
            isCorrect: opt.isCorrect
          }))
        };

        if (question.id) {
          await QuizService.updateQuestion(question.id, questionPayload);
        } else {
          await QuizService.addQuestion(quizId.value, questionPayload);
        }
      }

      savedQuiz = { id: quizId.value };
    } else {
      const response = await QuizService.create(payload);
      savedQuiz = response.data;

      for (const question of quizData.value.questions) {
        const questionPayload = {
          text: question.text,
          type: question.type,
          feedback: question.feedback,
          options: question.options.map(opt => ({
            text: opt.text,
            isCorrect: opt.isCorrect
          }))
        };

        await QuizService.addQuestion(savedQuiz.id, questionPayload);
      }
    }

    if (quizData.value.questions.length > 0) {
      const questionIds = quizData.value.questions
        .filter(q => q.id)
        .map(q => q.id);

      if (questionIds.length > 0) {
        await QuizService.reorderQuestions(
          savedQuiz.id,
          questionIds
        );
      }
    }

    Swal.fire({
      icon: 'success',
      title: 'Success',
      text: `Quiz ${isEditMode.value ? 'updated' : 'created'} successfully`
    });

    router.push('/caduceus/quizzes');
  } catch (err) {
    console.error('Error saving quiz:', err);
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: err.response?.data?.message || 'Failed to save quiz'
    });
  } finally {
    saving.value = false;
  }
};

onMounted(() => {
  fetchCourses();
  fetchQuiz();
});
</script>

<style scoped>
.question-card {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: #fff;
}

.question-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #e9ecef;
  background: #f8f9fa;
}

.drag-handle {
  cursor: move;
  font-size: 1.2rem;
  color: #6c757d;
}

.drag-handle:hover {
  color: #495057;
}

.question-number {
  font-weight: 600;
  color: #495057;
}

.question-body {
  padding: 1.5rem;
}

.option-item .input-group-text {
  min-width: 45px;
  justify-content: center;
}

.questions-list {
  min-height: 100px;
}

.add-course-label {
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #495057;
}
</style>
